import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const LOVABLE_URL = 'https://ogd.lovable.app/';

const MasterplanSection = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeActive, setIframeActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="masterplan"
      ref={sectionRef}
      className="relative bg-stone-950 py-28 lg:py-40"
      data-testid="masterplan-section"
    >
      {/* Inject CSS overrides for Mapbox popups within iframe context */}
      <style>{`
        .masterplan-iframe-container iframe {
          display: block;
        }
        .masterplan-iframe-container {
          position: relative;
          overflow: visible !important;
        }
        @media (max-width: 767px) {
          .masterplan-iframe-container iframe {
            touch-action: pan-y;
          }
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-emerald-500/40" />
            <span className="text-emerald-400/80 text-xs font-sans font-medium tracking-[0.3em] uppercase">
              {language === 'fr' ? 'Masterplan' : 'Masterplan'}
            </span>
            <span className="w-12 h-px bg-emerald-500/40" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {language === 'fr' ? 'Explorez nos ' : 'Explore our '}
            <span className="text-emerald-400">{language === 'fr' ? 'domaines' : 'estates'}</span>
          </h2>
          <p
            className="mt-6 text-white/70 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {language === 'fr'
              ? "Visualisez l'emplacement de votre futur investissement en temps réel."
              : 'Visualize the location of your future investment in real time.'}
          </p>
        </motion.div>

        {/* Iframe Container — NO overflow hidden */}
        <motion.div
          className="relative mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.2 }}
        >
          <div
            className="relative masterplan-iframe-container"
            style={{
              borderRadius: '24px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* Loading state */}
            {!iframeLoaded && (
              <div
                className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-stone-950"
                style={{ borderRadius: '24px' }}
              >
                <div className="w-10 h-10 border-2 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin mb-4" />
                <p className="text-white/50 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {language === 'fr' ? 'Chargement du masterplan...' : 'Loading masterplan...'}
                </p>
              </div>
            )}

            {/* Iframe */}
            <iframe
              src={LOVABLE_URL}
              title="One Green Dev - Masterplan Interactif"
              className="w-full block"
              style={{
                height: '85vh',
                minHeight: '500px',
                border: 'none',
                borderRadius: '24px',
                pointerEvents: (isMobile && !iframeActive) ? 'none' : 'auto',
              }}
              frameBorder="0"
              allow="fullscreen"
              allowFullScreen
              onLoad={() => setIframeLoaded(true)}
              data-testid="masterplan-iframe"
            />

            {/* Mobile touch overlay */}
            {isMobile && !iframeActive && iframeLoaded && (
              <div className="absolute inset-0 z-30 flex items-center justify-center" data-testid="masterplan-touch-overlay"
                onClick={() => setIframeActive(true)} style={{ borderRadius: '24px', background: 'rgba(0,0,0,0.15)' }}>
                <div className="bg-black/60 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <p className="text-white text-xs font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {language === 'fr' ? 'Touchez pour explorer la carte' : 'Tap to explore the map'}
                  </p>
                </div>
              </div>
            )}

            {/* Deactivate button (mobile) */}
            {isMobile && iframeActive && (
              <button onClick={() => setIframeActive(false)} data-testid="masterplan-deactivate-btn"
                className="absolute top-4 right-4 z-40 px-4 py-2 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all"
                style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)', fontFamily: "'Montserrat', sans-serif" }}>
                {language === 'fr' ? 'Fermer la carte' : 'Close map'}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MasterplanSection;
